// CategoryUniverse — editorial 6-card grid that broadens Farveladeland's
// identity beyond Murano. Each card uses lifestyle/collection imagery.

import { motion } from "framer-motion";
import type { CollectionWithProducts, ShopifyProduct } from "@/lib/shopify";
import { LocaleLink } from "@/components/LocaleLink";
import { useLocale } from "@/lib/i18n";

type Cat = {
  collection: CollectionWithProducts | null;
  handle: string;
  italian: string;
  labelDa: string;
  labelEn: string;
  captionDa: string;
  captionEn: string;
  span: string;
  fallback: string;
  imageOverride?: string;
};

function pickImage(c: CollectionWithProducts | null): string | null {
  if (!c) return null;
  if (c.image?.url) return c.image.url;
  const first = c.products?.edges?.[0] as ShopifyProduct | undefined;
  return first?.node?.images?.edges?.[0]?.node?.url ?? null;
}

interface Props {
  nyheder: CollectionWithProducts | null;
  bordet: CollectionWithProducts | null;
  belysning: CollectionWithProducts | null;
  interior: CollectionWithProducts | null;
  murano: CollectionWithProducts | null;
  unikke: CollectionWithProducts | null;
}

export function CategoryUniverse({
  nyheder,
  bordet,
  belysning,
  interior,
  murano,
  unikke,
}: Props) {
  const locale = useLocale();

  const cats: Cat[] = [
    {
      collection: nyheder,
      handle: "nyheder",
      italian: "Novità",
      labelDa: "Nyheder",
      labelEn: "New arrivals",
      captionDa: "Lige kommet hjem fra rejse.",
      captionEn: "Just home from a trip.",
      span: "md:col-span-8 md:row-span-1",
      fallback:
        "https://farveladeland.com/cdn/shop/collections/Murano_2.jpg?v=1731359701&width=1600",
    },
    {
      collection: bordet,
      handle: "borddaekning",
      italian: "A tavola",
      labelDa: "Til bordet",
      labelEn: "For the table",
      captionDa: "Tallerkener, glas og bestik med patina.",
      captionEn: "Plates, glassware and cutlery with patina.",
      span: "md:col-span-4",
      fallback:
        "https://farveladeland.com/cdn/shop/collections/Sarreguemines.jpg?v=1673351600&width=1200",
    },
    {
      collection: belysning,
      handle: "vintage-lamper",
      italian: "Illuminazione",
      labelDa: "Belysning",
      labelEn: "Lighting",
      captionDa: "Lofts-, bord- og væglamper fra et halvt århundrede.",
      captionEn: "Ceiling, table and wall lamps from half a century.",
      span: "md:col-span-4",
      fallback:
        "https://farveladeland.com/cdn/shop/collections/Screenshot_2022-08-20_at_14.12.10.png?v=1673351136&width=1200",
    },
    {
      collection: interior,
      handle: "interior",
      italian: "Interno",
      labelDa: "Interiør",
      labelEn: "Interior",
      captionDa: "Vaser, spejle, lysestager og små detaljer til hjemmet.",
      captionEn: "Vases, mirrors, candleholders and small details for the home.",
      span: "md:col-span-8",
      imageOverride:
        "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/8128DB93-9B98-4D43-9AC0-E22D8A8D8837.jpg?v=1763730968&width=1600",
      fallback:
        "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/8128DB93-9B98-4D43-9AC0-E22D8A8D8837.jpg?v=1763730968&width=1600",
    },
    {
      collection: murano,
      handle: "murano-lamper",
      italian: "Vetro di Murano",
      labelDa: "Murano",
      labelEn: "Murano",
      captionDa: "Vores hjerte — mundblæst glas fra Venedig.",
      captionEn: "Our heart — hand-blown glass from Venice.",
      span: "md:col-span-7",
      fallback:
        "https://farveladeland.com/cdn/shop/collections/Murano_2.jpg?v=1731359701&width=1600",
    },
    {
      collection: unikke,
      handle: "sidste-chance-farvelader",
      italian: "Pezzi unici",
      labelDa: "Unikke fund",
      labelEn: "Unique finds",
      captionDa: "Én af én. Når det er væk, er det væk.",
      captionEn: "One of a kind. When it's gone, it's gone.",
      span: "md:col-span-5",
      fallback:
        "https://farveladeland.com/cdn/shop/files/IMG_6571.jpg?v=1700000000&width=1200",
    },
  ];

  return (
    <section className="mx-auto max-w-[1500px] px-6 pb-16 pt-12 md:px-10 md:pb-32 md:pt-28">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mb-12 max-w-2xl md:mb-16"
      >
        <p className="eyebrow">{locale === "da" ? "Et kurateret univers" : "A curated universe"}</p>
        <h2 className="mt-4 font-display text-[clamp(2rem,5vw,4rem)] leading-[1.02]">
          {locale === "da" ? "Find dit næste " : "Find your next "}
          <span className="display-italic text-terracotta">
            {locale === "da" ? "stykke." : "piece."}
          </span>
        </h2>
      </motion.div>

      <div className="grid auto-rows-[min(58vw,440px)] grid-cols-1 gap-3 md:grid-cols-12 md:gap-4">
        {cats.map((c, i) => {
          const src = c.imageOverride ?? pickImage(c.collection) ?? c.fallback;
          return (
            <motion.div
              key={c.handle}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden rounded-sm bg-muted ${c.span}`}
            >
              <LocaleLink
                to="/collections/$handle"
                params={{ handle: c.handle }}
                className="block h-full w-full"
              >
                <img
                  src={src}
                  alt={locale === "da" ? c.labelDa : c.labelEn}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 40%, oklch(0.14 0.01 50 / 0.18) 65%, oklch(0.14 0.01 50 / 0.78) 100%)",
                  }}
                  aria-hidden
                />
                <div className="absolute left-6 top-6 md:left-8 md:top-8">
                  <p className="text-[10px] uppercase tracking-[0.32em] text-background/85">
                    {c.italian}
                  </p>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-9">
                  <h3 className="font-display text-3xl leading-tight text-background md:text-5xl">
                    {locale === "da" ? c.labelDa : c.labelEn}
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-background/80 md:text-[15px]">
                    {locale === "da" ? c.captionDa : c.captionEn}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-background">
                    <span className="h-px w-8 bg-background/60 transition-all duration-300 group-hover:w-14 group-hover:bg-amber-glass" />
                    {locale === "da" ? "Se udvalget" : "Explore"}
                  </span>
                </div>
              </LocaleLink>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
